// Custom confirmation dialog for Turbo (replaces the native window.confirm).
//
// Turbo invokes config.forms.confirm for any element carrying data-turbo-confirm:
// links with data-turbo-method, <form>s, and button_to. It is awaited and must
// resolve to a boolean. We render a Bootstrap 5 modal for a consistent look.
//
// Message rendering: a message starting with "<" is treated as template-authored
// HTML (lists, alerts — e.g. the volume publish confirm) and rendered via
// innerHTML after a defensive sanitize pass (no scripts / event handlers /
// javascript: URLs); anything else is plain text with newlines preserved
// (pre-line). Author HTML confirms in ERB so interpolations stay auto-escaped;
// never feed raw user-generated content into data-turbo-confirm.
import { Turbo } from '@hotwired/turbo-rails'

function el(tag, attrs = {}, children = []) {
  const node = document.createElement(tag)
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = v
    else node.setAttribute(k, v)
  }
  for (const child of children) {
    node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child)
  }
  return node
}

// Defense-in-depth for the HTML message mode: the content is template-authored,
// but strip active content anyway so a slip (unescaped interpolation) cannot
// execute — scripts, embeds, on* handlers and javascript: URLs are removed.
function sanitizeHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('script, style, iframe, object, embed').forEach((node) => node.remove())
  doc.querySelectorAll('*').forEach((node) => {
    for (const attr of [...node.attributes]) {
      const name = attr.name.toLowerCase()
      const value = attr.value.trim().toLowerCase()
      if (name.startsWith('on')) node.removeAttribute(attr.name)
      else if (['href', 'src', 'xlink:href'].includes(name) && value.startsWith('javascript:')) node.removeAttribute(attr.name)
    }
  })
  return doc.body.innerHTML
}

function buildModal() {
  // div, not p: HTML messages may contain block elements (lists, alerts)
  const messageEl = el('div', { class: 'mb-0', 'data-confirm-message': '' })
  const okBtn = el('button', { type: 'button', class: 'btn btn-danger', 'data-confirm-ok': '' }, ['Confirm'])

  const dialog = el('div', { class: 'modal-dialog modal-dialog-centered' }, [
    el('div', { class: 'modal-content' }, [
      el('div', { class: 'modal-header' }, [
        el('h5', { class: 'modal-title', id: 'turbo-confirm-title' }, ['Please confirm']),
        el('button', { type: 'button', class: 'btn-close', 'data-bs-dismiss': 'modal', 'aria-label': 'Close' })
      ]),
      el('div', { class: 'modal-body' }, [messageEl]),
      el('div', { class: 'modal-footer' }, [
        el('button', { type: 'button', class: 'btn btn-light', 'data-bs-dismiss': 'modal' }, ['Cancel']),
        okBtn
      ])
    ])
  ])

  const modal = el('div', {
    class: 'modal fade', tabindex: '-1', id: 'turbo-confirm-modal',
    'aria-hidden': 'true', 'aria-labelledby': 'turbo-confirm-title'
  }, [dialog])

  document.body.appendChild(modal)
  return modal
}

// Promise<boolean> confirm dialog. Used by Turbo (data-turbo-confirm) and reused
// directly by controllers that perform their own fetch (e.g. bulk actions).
export function confirmDialog(message) {
  return new Promise((resolve) => {
    const modalEl = document.getElementById('turbo-confirm-modal') || buildModal()
    const messageEl = modalEl.querySelector('[data-confirm-message]')
    if (String(message).trimStart().startsWith('<')) {
      messageEl.innerHTML = sanitizeHtml(message) // template HTML (see header comment)
      messageEl.style.whiteSpace = ''
    } else {
      messageEl.textContent = message
      messageEl.style.whiteSpace = 'pre-line' // keep the \n breaks plain confirms rely on
    }
    const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl)
    const okBtn = modalEl.querySelector('[data-confirm-ok]')

    let settled = false
    const finish = (result) => {
      if (settled) return
      settled = true
      okBtn.removeEventListener('click', onOk)
      modalEl.removeEventListener('hidden.bs.modal', onHidden)
      resolve(result)
    }
    const onOk = () => { modal.hide(); finish(true) }
    const onHidden = () => finish(false)

    okBtn.addEventListener('click', onOk)
    modalEl.addEventListener('hidden.bs.modal', onHidden)
    modalEl.addEventListener('shown.bs.modal', () => okBtn.focus(), { once: true })
    modal.show()
  })
}

Turbo.config.forms.confirm = confirmDialog
